/**
 * Wire shape for the FE's soft ``user_context`` signal — what the user
 * is currently looking at (OR-6021).
 *
 * Distinct from ``WireScope`` (`scopeWire.ts`) in three ways:
 *
 *   1. **Emitter**: produced by the FE from the URL only. The BE never
 *      emits a ``user_context`` back at the client.
 *   2. **Meaning**: a soft hint. The orchestrator treats it as cue,
 *      not as a routing or tool-arg key.
 *   3. **Kinds**: omits the ``*_draft`` variants. A draft has no URL
 *      the user can be on, so it can never be a ``user_context``.
 *
 * Wire keys are snake_case to match the BE Pydantic mirror at
 * ``libs/backend/services/epi-study-agent/epi_study_agent/user_context.py``.
 * Keep the two in lockstep — this Zod schema is the trust boundary.
 */
import { z } from 'zod';

const ProjectUserContextWireSchema = z.object({
  kind: z.literal('project'),
  project_id: z.string().min(1),
});

const CohortUserContextWireSchema = z.object({
  kind: z.literal('cohort'),
  project_id: z.string().min(1),
  cohort_id: z.string().min(1),
});

const VariableUserContextWireSchema = z.object({
  kind: z.literal('variable'),
  project_id: z.string().min(1),
  variable_id: z.string().min(1),
});

const ConceptGroupUserContextWireSchema = z.object({
  kind: z.literal('concept_group'),
  project_id: z.string().min(1),
  concept_group_id: z.string().min(1),
});

const DatasetUserContextWireSchema = z.object({
  kind: z.literal('dataset'),
  project_id: z.string().min(1),
  dataset_id: z.string().min(1),
});

export const WireUserContextSchema = z.discriminatedUnion('kind', [
  ProjectUserContextWireSchema,
  CohortUserContextWireSchema,
  VariableUserContextWireSchema,
  ConceptGroupUserContextWireSchema,
  DatasetUserContextWireSchema,
]);

export type WireUserContext = z.infer<typeof WireUserContextSchema>;

/**
 * The set of ``WireScope`` kinds that are also valid ``WireUserContext``
 * kinds. Used by ``toWireUserContext`` below to safely narrow the
 * existing URL-derived ``WireScope`` into a ``WireUserContext`` (or
 * ``undefined`` when the scope is a draft).
 */
const USER_CONTEXT_KINDS = new Set(['project', 'cohort', 'variable', 'concept_group', 'dataset']);

/**
 * Narrow a ``WireScope`` to a ``WireUserContext`` when possible.
 *
 * Reusing the URL-derived ``WireScope`` rather than recomputing from
 * the route keeps the source of truth in one place — the
 * ``feature-project-page`` URL parser. Drafts (which a ``WireScope``
 * can carry when the agent has declared one) become ``undefined`` here
 * because the user, by construction, cannot be looking at a draft.
 *
 * Returns ``undefined`` when ``scope`` is ``undefined`` or carries a
 * ``*_draft`` kind. Callers treat ``undefined`` as "no user_context
 * on the wire this turn" — same wire shape as a legacy client.
 */
export const toWireUserContext = (
  scope: { kind: string } | undefined
): WireUserContext | undefined => {
  if (!scope) return undefined;
  if (!USER_CONTEXT_KINDS.has(scope.kind)) return undefined;
  // Parse rather than cast — the schema is a trust boundary even on
  // the outgoing path so a future drift between WireScope and
  // WireUserContext shapes fails at runtime instead of silently
  // shipping malformed payloads.
  const parsed = WireUserContextSchema.safeParse(scope);
  return parsed.success ? parsed.data : undefined;
};
